import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Play, RotateCcw, CheckCircle, AlertTriangle, XCircle, Calculator } from 'lucide-react';
import { Variable } from '../types/model.types';
import { VariableType } from '@/types';
import { calculateVariables } from '../utils/formula-evaluator';
import { getGlobalDecimalPlaces } from '@/lib/utils/format';

interface ModelSimulatorProps {
  variables: Variable[];
}

export function ModelSimulator({ variables }: ModelSimulatorProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Input variables (non-calculated)
  const inputVars = useMemo(
    () => variables.filter((v) => v.variableType !== VariableType.CALCULATED && v.code),
    [variables]
  );

  // Calculated variables
  const calculatedVars = useMemo(
    () => variables.filter((v) => v.variableType === VariableType.CALCULATED && v.code),
    [variables]
  );

  // Input state — initialize with default values
  const [inputs, setInputs] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const v of inputVars) {
      init[v.code!] = v.defaultValue != null && v.defaultValue !== '' ? String(v.defaultValue) : '';
    }
    return init;
  });

  const [hasRun, setHasRun] = useState(false);

  // Run calculation
  const results = useMemo(() => {
    if (!hasRun) return null;

    // Build numeric input values
    const inputValues: Record<string, any> = {};
    for (const v of inputVars) {
      const raw = inputs[v.code!];
      if (raw === '' || raw == null) continue;
      inputValues[v.code!] = Number(raw);
    }

    return calculateVariables(variables, inputValues);
  }, [hasRun, inputs, variables, inputVars]);

  // Validation: check for issues before running
  const validation = useMemo(() => {
    const issues: { code: string; message: string; severity: 'error' | 'warning' }[] = [];

    // Check empty required inputs
    for (const v of inputVars) {
      const val = inputs[v.code!];
      if ((!val || val === '') && v.isRequired) {
        issues.push({ code: v.code!, message: `${v.name} es requerido`, severity: 'error' });
      }
    }

    // Check calculated vars without formula
    for (const v of calculatedVars) {
      if (!v.formula) {
        issues.push({ code: v.code!, message: `${v.name} no tiene fórmula`, severity: 'error' });
      }
    }

    // Check if there are any variables at all
    if (variables.length === 0) {
      issues.push({ code: '', message: 'No hay variables definidas', severity: 'error' });
    }

    return issues;
  }, [inputVars, calculatedVars, inputs, variables]);

  const errors = validation.filter((i) => i.severity === 'error');
  const warnings = validation.filter((i) => i.severity === 'warning');

  const handleRun = () => setHasRun(true);

  const handleReset = () => {
    setHasRun(false);
    const init: Record<string, string> = {};
    for (const v of inputVars) {
      init[v.code!] = v.defaultValue != null && v.defaultValue !== '' ? String(v.defaultValue) : '';
    }
    setInputs(init);
  };

  const handleInputChange = (code: string, value: string) => {
    setInputs((prev) => ({ ...prev, [code]: value }));
    if (hasRun) setHasRun(true); // re-trigger calculation
  };

  // Get result for a variable
  const getResult = (code: string) => results?.find((r) => r.variableCode === code);

  const formatValue = (val: any, type: string) => {
    if (val == null) return '—';
    const num = Number(val);
    if (isNaN(num)) return String(val);
    const dp = getGlobalDecimalPlaces();
    if (type === VariableType.PERCENTAGE) return `${num.toFixed(dp)}%`;
    if (type === VariableType.CURRENCY)
      return `Bs ${num.toLocaleString('es-BO', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
    return num.toLocaleString('es-BO', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  };

  if (!isOpen) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-4">
          <button
            onClick={() => setIsOpen(true)}
            className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <Play className="w-4 h-4" />
            Abrir simulador — probar el modelo con valores de ejemplo
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Calculator className="w-5 h-5 text-primary" />
            Simulador del Modelo
          </CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleReset}>
              <RotateCcw className="w-3 h-3 mr-1" /> Reset
            </Button>
            <Button size="sm" onClick={handleRun} disabled={errors.length > 0}>
              <Play className="w-3 h-3 mr-1" /> {hasRun ? 'Recalcular' : 'Calcular'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setIsOpen(false)}>
              Cerrar
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Ingresá valores de prueba y verificá que las fórmulas calculen correctamente.
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Validation issues */}
        {(errors.length > 0 || warnings.length > 0) && (
          <div className="space-y-1">
            {errors.map((e, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-bloqueo">
                <XCircle className="w-3 h-3 shrink-0" />
                <span>
                  <strong>{e.code}</strong> {e.message}
                </span>
              </div>
            ))}
            {warnings.map((w, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-alerta">
                <AlertTriangle className="w-3 h-3 shrink-0" />
                <span>
                  <strong>{w.code}</strong> {w.message}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left: Inputs */}
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-info" />
              Variables de entrada ({inputVars.length})
            </h3>
            {inputVars.some((v) => v.variableType === VariableType.PERCENTAGE) && (
              <div className="mb-3 p-2 rounded bg-info-suave border border-info-borde text-[11px] text-primary ">
                <strong>Nota:</strong> Las variables tipo % se convierten automáticamente a decimal
                en las fórmulas (ej: 25% → 0.25). No uses{' '}
                <code className="bg-info-suave px-1 rounded">/ 100</code> en la fórmula.
              </div>
            )}
            <div className="space-y-2">
              {inputVars.map((v) => (
                <div key={v.code} className="flex items-center gap-2">
                  <label
                    className="text-xs font-mono text-muted-foreground w-40 truncate shrink-0"
                    title={v.name}
                  >
                    {v.code}
                  </label>
                  <Input
                    type="number"
                    step="any"
                    value={inputs[v.code!] || ''}
                    onChange={(e) => handleInputChange(v.code!, e.target.value)}
                    placeholder={v.defaultValue != null ? String(v.defaultValue) : '0'}
                    className="h-8 text-xs font-mono"
                  />
                  <span className="text-[10px] text-muted-foreground w-16 shrink-0">
                    {v.variableType === VariableType.PERCENTAGE
                      ? '%'
                      : v.variableType === VariableType.CURRENCY
                        ? 'BOB'
                        : ''}
                  </span>
                </div>
              ))}
              {inputVars.length === 0 && (
                <p className="text-xs text-muted-foreground">No hay variables de entrada</p>
              )}
            </div>
          </div>

          {/* Right: Results */}
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-ok" />
              Resultados ({calculatedVars.length} calculadas)
            </h3>
            {!hasRun ? (
              <div className="text-center py-8 text-muted-foreground text-sm border rounded-lg border-dashed">
                <Calculator className="w-8 h-8 mx-auto mb-2 opacity-30" />
                Hacé click en "Calcular" para ver resultados
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/60">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Variable</th>
                      <th className="text-right px-3 py-2 font-semibold">Valor</th>
                      <th className="text-center px-3 py-2 font-semibold w-8">OK</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {/* Show input values first */}
                    {inputVars.map((v) => {
                      const val = inputs[v.code!];
                      const isPct = v.variableType === VariableType.PERCENTAGE;
                      const numVal = val ? Number(val) : null;
                      return (
                        <tr key={v.code} className="bg-info-suave ">
                          <td className="px-3 py-1.5">
                            <span className="font-mono font-medium">{v.code}</span>
                            <span className="text-muted-foreground ml-1">({v.name})</span>
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono">
                            {val ? (
                              <>
                                {formatValue(val, v.variableType)}
                                {isPct && numVal != null && (
                                  <span className="text-[10px] text-muted-foreground ml-1">
                                    → {(numVal / 100).toFixed(4)}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            <span
                              className="w-2 h-2 rounded-full bg-info inline-block"
                              title="Input"
                            />
                          </td>
                        </tr>
                      );
                    })}
                    {/* Calculated results */}
                    {calculatedVars.map((v) => {
                      const r = getResult(v.code!);
                      const hasError = !!r?.error;
                      const value = r?.value;
                      return (
                        <tr key={v.code} className={hasError ? 'bg-bloqueo-suave ' : ''}>
                          <td className="px-3 py-1.5">
                            <span className="font-mono font-medium">{v.code}</span>
                            <span className="text-muted-foreground ml-1">({v.name})</span>
                            {v.formula && (
                              <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                                = {v.formula}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono font-semibold">
                            {hasError ? (
                              <span className="text-bloqueo text-[10px] font-normal">
                                {r.error}
                              </span>
                            ) : (
                              formatValue(value, v.variableType)
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            {hasError ? (
                              <XCircle className="w-3.5 h-3.5 text-bloqueo inline" />
                            ) : value != null ? (
                              <CheckCircle className="w-3.5 h-3.5 text-ok inline" />
                            ) : (
                              <AlertTriangle className="w-3.5 h-3.5 text-alerta inline" />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Summary */}
            {hasRun && results && (
              <div className="mt-3 flex gap-2">
                <Badge variant="outline" className="text-xs">
                  <CheckCircle className="w-3 h-3 mr-1 text-ok" />
                  {results.filter((r) => !r.error && r.value != null).length} OK
                </Badge>
                {results.filter((r) => r.error).length > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    <XCircle className="w-3 h-3 mr-1" />
                    {results.filter((r) => r.error).length} errores
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
